import React, { useState } from 'react';
import * as consts from 'config/consts';
import { formatNumber } from 'utils/formatter';
import { DividendProps } from 'pages/DividendPrediction';
import {
  DividendListProps,
  DividendListItemProps,
} from './DividendClassification';
import { getSummaryDividendHaito } from './DividendTransition';
import { DividendDetailInfo } from '../dividendDetailPrediction/DividendDetailInfo';
import { AccountType, AccountTypeLabel } from '../utils/accountType';

export const AccountTypeLabelList = ({
  list,
}: {
  list: DividendListItemProps[];
}) => {
  return (
    <>
      {list.map((list, i) => {
        if (i <= 1) {
          return (
            <AccountTypeLabel
              accountType={list.accountType as AccountType}
              className="ml-1"
              key={i}
            />
          );
        } else if (i === 2) {
          return (
            <div className="ml-1 text-1.5xs text-bluegray-500" key={i}>
              ...他
            </div>
          );
        } else {
          return <React.Fragment key={i}></React.Fragment>;
        }
      })}
    </>
  );
};

export const accountTypeSortList = [
  {
    accountType: consts.AccountType.NISA,
    num: 1,
  },
  {
    accountType: consts.AccountType.旧NISA,
    num: 1,
  },
  {
    accountType: consts.AccountType['NISA（つみたて投資枠）'],
    num: 2,
  },
  {
    accountType: consts.AccountType['NISA（成長投資枠）'],
    num: 3,
  },
  {
    accountType: consts.AccountType.特定,
    num: 4,
  },
  {
    accountType: consts.AccountType.一般,
    num: 5,
  },
  {
    accountType: consts.AccountType.企業型,
    num: 6,
  },
  {
    accountType: consts.AccountType.iDeCo,
    num: 6,
  },
  {
    accountType: consts.AccountType.その他,
    num: 7,
  },
];
export const sortDividendListAccountType = (
  data: Array<DividendListItemProps | DividendProps>
): Array<DividendListItemProps | DividendProps> => {
  const sortData = data.sort((first, second) => {
    const firstNum = accountTypeSortList.find(
      list => list.accountType === first.accountType
    )?.num;
    const secondNum = accountTypeSortList.find(
      list => list.accountType === second.accountType
    )?.num;

    if (firstNum === undefined || secondNum === undefined) {
      return 0;
    } else if (firstNum > secondNum) {
      return 1;
    } else if (firstNum < secondNum) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortData;
};

const DividendDetailItem = ({
  dividendData,
  isTaxDeduction,
}: {
  dividendData: DividendListProps;
  isTaxDeduction: boolean;
}) => {
  const sortedDividendItems = sortDividendListAccountType(
    dividendData.items
  ) as DividendListItemProps[];
  const sortedDividendData = {
    ...dividendData,
    items: sortedDividendItems,
  } as DividendListProps;

  const summaryHaito = getSummaryDividendHaito(
    dividendData.items,
    isTaxDeduction
  );

  const isIncludeUnreleasedDividend = dividendData.items.some(
    item =>
      item.unreleasedStatus === 'all' || item.unreleasedStatus === 'not_all'
  );
  const isAllUnreleasedDividend = dividendData.items.every(
    item => item.unreleasedStatus === 'all'
  );
  const unreleasedStatus =
    !isIncludeUnreleasedDividend && !isAllUnreleasedDividend
      ? 'not_exist'
      : isIncludeUnreleasedDividend && !isAllUnreleasedDividend
      ? 'not_all'
      : 'all';

  const isHasNew = dividendData.items.some(item => item.hasNew);

  // 配当詳細スライド
  const [showDetailInfo, setShowDetailInfo] = useState<boolean>(false);
  const [hideAnimate, setHideAnimate] = useState<boolean>(false);

  const detailInfoShow = (): void => {
    setShowDetailInfo(true);
    setHideAnimate(false);
  };

  const detailInfoHide = (): void => {
    setShowDetailInfo(false);
  };

  const detailInfoHideAnimate = (): void => {
    setHideAnimate(true);
  };

  return (
    <>
      <div className="px-4 py-3 border-b border-gray-300">
        <div
          className="grid items-enter"
          style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="ml-2">
            <div className="flex flex-row">
              <div className="text-xs text-bluegray-500">
                {dividendData.symbol}
              </div>
              <AccountTypeLabelList {...{ list: sortedDividendData.items }} />
            </div>
            <div className="flex flex-col line-clamp-2">
              <div className="mt-2 font-bold text-bluegray-700">
                {dividendData.symbolName}
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center">
            <div className="ml-auto" style={{ whiteSpace: 'nowrap' }}>
              <div className="flex flex-col">
                <div
                  className={`${
                    unreleasedStatus === 'all'
                      ? 'text-bluegray-400'
                      : 'text-bluegray-700'
                  }`}>
                  {formatNumber(summaryHaito, {})}
                  <span className="ml-1 text-xs">円</span>
                </div>
                {unreleasedStatus !== 'not_exist' ? (
                  <div className="ml-auto text-1.5xs text-bluegray-400">
                    (未発表
                    {unreleasedStatus === 'not_all' ? '含む' : ''})
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div
              className="flex items-center justify-center w-10 ml-2 cursor-pointer"
              onClick={
                !showDetailInfo ? detailInfoShow : detailInfoHideAnimate
              }>
              <img src="/static/images/dividendInfo_button.svg" alt="" />
              {isHasNew ? (
                <span className="rounded-full h-2 w-2 bg-tradewell -mt-5 -ml-2 z-10"></span>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
      {showDetailInfo ? (
        <DividendDetailInfo
          {...{
            detailInfoHide,
            hideAnimate,
            dividendData: sortedDividendData,
            isTaxDeduction,
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export const DividendSymbolList = ({
  dividendList,
  totalDividend,
  isTaxDeduction,
}: {
  dividendList: DividendListProps[];
  totalDividend: number;
  isTaxDeduction: boolean;
}) => {
  return (
    <>
      <div
        className="grid mt-4 px-4 py-1 text-sm text-bluegray-550 font-bold bg-gray-300"
        style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="ml-2">銘柄</div>
        <div>予想額</div>
      </div>
      {dividendList.map((data, i) => (
        <DividendDetailItem
          {...{ dividendData: data, isTaxDeduction }}
          key={i}
        />
      ))}
      <div className="flex flex-row items-center pl-6 pr-4 pt-2 pb-8">
        <div className="font-bold">合計</div>
        <div className="ml-auto">
          <span className="mr-1 text-lg font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>
      </div>
    </>
  );
};
